/**
 * 통합관리자 — 운영 현황
 * ---------------------------------------------------------------------------
 * 운영자가 아침에 한 번 보고 "오늘 신경 쓸 게 있나"를 판단하는 화면이다.
 *
 * 배치 순서에 의도가 있다.
 *   1. 조치가 필요한 것 (한도 초과, 연체, 로그인 실패 급증) — 맨 위
 *   2. 전체 규모 (기업·사용자·용량·트래픽)
 *   3. 기업별 상세 표
 *
 * 개인정보는 다루지 않는다. 어느 기업이 얼마나 쓰는지는 보이지만 그 기업의
 * 구성원이 누구인지는 보이지 않는다. 그건 지원 모드의 영역이다.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Card,
  Col,
  Progress,
  Radio,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Button } from 'antd';
import { Link } from 'react-router-dom';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { CodeTag, useCodes } from '../../lib/codes';
import type { Catalog } from '../../lib/catalog';

const { Text, Paragraph } = Typography;

interface Summary {
  tenantTotal: number;
  tenantActive: number;
  tenantSuspended: number;
  userTotal: number;
  userActive: number;
  userActiveToday: number;
  sessionsLive: number;
  storageBytes: number;
  egressBytesThisMonth: number;
  fileCount: number;
  quotaExceededCount: number;
  outstandingAmount: number;
  overdueAmount: number;
}

interface TenantUsage {
  tenantId: string;
  companyName: string;
  slug: string;
  status: string;
  userCount: number;
  activeUserCount: number;
  storageBytes: number;
  storageLimit: number | null;
  /** 보관 중인 파일 개수. 용량과 짝으로 읽는다 */
  fileCount: number;
  egressBytes: number;
  egressLimit: number | null;
  exceededMetrics: string[];
  lastLoginAt: string | null;
}

/**
 * 이 표에 몇 줄까지 보일 것인가.
 *
 * 여기는 아침에 한 번 훑는 자리다. 고객이 늘수록 표가 길어져 아래 내용이
 * 밀려나므로 **위에서 몇 곳만** 보여주고, 전부 보려면 전용 화면으로 보낸다.
 */
const TOP_N = 10;

type Sort = 'storage' | 'egress' | 'users';

/** 무엇을 기준으로 추린 상위인지 말해 준다 — 안 적으면 순서를 오해한다. */
function currentSortLabel(sort: Sort): string {
  if (sort === 'egress') return '트래픽';
  if (sort === 'users') return '사용자 수';
  return '저장 용량';
}

interface LoginDay {
  day: string;
  success: number;
  failure: number;
  distinct_users: number;
}

/** 최근 24시간 이상 징후. 숫자 하나로 "봐야 하나"를 판단하게 한다. */
interface Risk {
  loginFailures24h: number;
  failureIps24h: number;
  deniedActions24h: number;
  openAlerts: number;
  supportSessionsLive: number;
  messageFailures24h: number;
  webhookFailures24h: number;
}

interface AuditRow {
  auditLogId: string;
  occurredAt: string;
  tenantId: string | null;
  companyName: string | null;
  category: string;
  action: string;
  actorType: string;
  result: string;
  resourceType: string | null;
  resourceLabel: string | null;
  supportSessionId: string | null;
}

/** 표시명과 색은 공통코드가 정한다(「시스템 관리 > 공통코드」). */
const CODE_GROUPS = ['AUDIT_CATEGORY', 'AUDIT_RESULT', 'ACTOR_TYPE', 'TENANT_STATUS'];

export default function DashboardPage() {
  const client = useQueryClient();
  const codes = useCodes(CODE_GROUPS);
  const [sort, setSort] = useState<Sort>('storage');

  /*
   * 계량 항목 이름은 공통코드가 아니라 metering.metric_definition 이 주인이다.
   * 공통코드로 복사해 두면 지표를 하나 늘릴 때 두 곳을 고쳐야 한다.
   */
  const catalog = useQuery<Catalog, NormalizedError>({
    queryKey: ['admin', 'catalog'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => (await apiClient.get<Catalog>('/admin/catalog')).data,
  });
  const metricLabel = (key: string) =>
    catalog.data?.metrics.find((m) => m.value === key)?.label ?? key;

  const summary = useQuery<Summary, NormalizedError>({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => (await apiClient.get<Summary>('/admin/dashboard')).data,
  });

  const tenants = useQuery<TenantUsage[], NormalizedError>({
    queryKey: ['admin', 'dashboard', 'tenants', sort],
    queryFn: async () =>
      (await apiClient.get<TenantUsage[]>('/admin/dashboard/tenants', { params: { sort } })).data,
  });

  const logins = useQuery<LoginDay[], NormalizedError>({
    queryKey: ['admin', 'dashboard', 'logins'],
    queryFn: async () =>
      (await apiClient.get<LoginDay[]>('/admin/dashboard/logins', { params: { days: 14 } })).data,
  });

  const risk = useQuery<Risk, NormalizedError>({
    queryKey: ['admin', 'dashboard', 'risk'],
    queryFn: async () => (await apiClient.get<Risk>('/admin/dashboard/risk')).data,
  });

  const audit = useQuery<AuditRow[], NormalizedError>({
    queryKey: ['admin', 'dashboard', 'audit'],
    queryFn: async () =>
      (await apiClient.get<AuditRow[]>('/admin/dashboard/audit', { params: { limit: 12 } })).data,
  });

  const s = summary.data;
  const loginDays = logins.data ?? [];
  const recentFailures = loginDays.slice(-3).reduce((sum, d) => sum + d.failure, 0);
  const recentSuccess = loginDays.slice(-3).reduce((sum, d) => sum + d.success, 0);

  // 실패가 성공보다 많으면 무차별 대입일 수 있다. 절대값이 작을 땐 의미가 없다.
  const loginAnomaly = recentFailures > 20 && recentFailures > recentSuccess * 2;

  const columns: ColumnsType<TenantUsage> = [
    {
      title: '기업',
      key: 'company',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <strong>{row.companyName}</strong>
            {row.status !== 'ACTIVE' && (
              <CodeTag codes={codes} group="TENANT_STATUS" value={row.status} />
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.slug}
          </Text>
        </Space>
      ),
    },
    {
      title: '사용자',
      key: 'users',
      width: 120,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.activeUserCount}명</span>
          {row.userCount !== row.activeUserCount && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              전체 {row.userCount}명
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '저장 용량',
      key: 'storage',
      width: 200,
      render: (_, row) => <UsageBar used={row.storageBytes} limit={row.storageLimit} bytes />,
    },
    {
      /*
        용량과 나란히 둔다. 같은 1GB 라도 큰 파일 열 개와 작은 파일 십만 개는
        백업·이관에 드는 품이 전혀 다르다.
      */
      title: '파일 수',
      key: 'files',
      width: 110,
      align: 'right',
      render: (_, row) =>
        row.fileCount > 0 ? (
          `${row.fileCount.toLocaleString('ko-KR')}개`
        ) : (
          <Text type="secondary">없음</Text>
        ),
    },
    {
      title: '이번 달 트래픽',
      key: 'egress',
      width: 200,
      render: (_, row) => <UsageBar used={row.egressBytes} limit={row.egressLimit} bytes />,
    },
    {
      title: '최근 접속',
      dataIndex: 'lastLoginAt',
      key: 'lastLogin',
      width: 130,
      render: (v: string | null) =>
        v ? (
          <Tooltip title={new Date(v).toLocaleString('ko-KR')}>
            {relativeDays(v)}
          </Tooltip>
        ) : (
          <Text type="secondary">없음</Text>
        ),
    },
    {
      title: '한도',
      key: 'exceeded',
      width: 160,
      render: (_, row) =>
        row.exceededMetrics.length === 0 ? (
          <Tag color="green">정상</Tag>
        ) : (
          <Space size={4} wrap>
            {row.exceededMetrics.map((m) => (
              <Tag color="red" key={m}>
                {metricLabel(m)} 초과
              </Tag>
            ))}
          </Space>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* ---- 조치가 필요한 것 ---- */}
      {/*
        여기 세는 것은 **막힌** 기업뿐이다.

        포함량을 넘겨 추가 과금되는 것은 사고가 아니라 매출이라 띄우지
        않는다. 둘을 섞어 세던 때는 AI 보고서를 한 건만 써도(포함 0건
        종량제) 조치 대상으로 떴다.
      */}
      {(s?.quotaExceededCount ?? 0) > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${s?.quotaExceededCount}곳이 한도에 막혔습니다`}
          description="초과 사용이 허용되지 않는 항목이라 그 기능이 멈춰 있습니다. 요금제를 올리거나 한도를 조정해야 풀립니다. 아래 표에서 확인하세요."
        />
      )}
      {(s?.overdueAmount ?? 0) > 0 && (
        <Alert
          type="error"
          showIcon
          message={`연체 금액 ${won(s?.overdueAmount)}`}
          description="과금 관리 화면에서 청구서와 수납 상태를 확인하세요."
        />
      )}
      {loginAnomaly && (
        <Alert
          type="error"
          showIcon
          message="로그인 실패가 비정상적으로 많습니다"
          description={`최근 3일 실패 ${recentFailures}회 / 성공 ${recentSuccess}회. 무차별 대입 시도일 수 있습니다.`}
        />
      )}

      {summary.isError && <Alert type="error" showIcon message={errorText(summary.error)} />}

      {/* ---- 전체 규모 ---- */}
      <Row gutter={16}>
        <Col span={6}>
          <Card size="small" loading={summary.isLoading}>
            <Statistic title="기업" value={s?.tenantActive ?? 0} suffix={`/ ${s?.tenantTotal ?? 0}곳`} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              정지 {s?.tenantSuspended ?? 0}곳
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" loading={summary.isLoading}>
            <Statistic title="활성 사용자" value={s?.userActive ?? 0} suffix={`/ ${s?.userTotal ?? 0}명`} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              24시간 내 접속 {s?.userActiveToday ?? 0}명 · 현재 세션 {s?.sessionsLive ?? 0}
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" loading={summary.isLoading}>
            <Statistic title="저장 용량" value={formatBytes(s?.storageBytes ?? 0)} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              파일 {(s?.fileCount ?? 0).toLocaleString('ko-KR')}개
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" loading={summary.isLoading}>
            <Statistic
              title="이번 달 다운로드 트래픽"
              value={formatBytes(s?.egressBytesThisMonth ?? 0)}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              미수금 {won(s?.outstandingAmount)}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* ---- 최근 24시간 이상 징후 ---- */}
      <Card size="small" title="최근 24시간" loading={risk.isLoading}>
        <Row gutter={16}>
          {[
            { label: '로그인 실패', value: risk.data?.loginFailures24h, warn: 100, to: 'login' },
            { label: '실패 IP 수', value: risk.data?.failureIps24h, warn: 20, to: 'login' },
            { label: '권한 거부', value: risk.data?.deniedActions24h, warn: 10, to: 'audit' },
            { label: '열린 알람', value: risk.data?.openAlerts, warn: 1, to: 'alert' },
            { label: '지원 모드 세션', value: risk.data?.supportSessionsLive, warn: 1, to: 'audit' },
            { label: '발송 실패', value: risk.data?.messageFailures24h, warn: 1, to: 'message' },
            { label: '웹훅 오류', value: risk.data?.webhookFailures24h, warn: 1, to: 'webhook' },
          ].map((item) => (
            <Col key={item.label} flex="1 1 0">
              <Link to="/app/admin-logs">
                <Statistic
                  title={item.label}
                  value={item.value ?? 0}
                  valueStyle={
                    (item.value ?? 0) >= item.warn ? { color: '#cf1322' } : { color: '#389e0d' }
                  }
                />
              </Link>
            </Col>
          ))}
        </Row>
      </Card>

      {/* ---- 로그인 추이 ---- */}
      <Card size="small" title="로그인 추이 (최근 14일)" loading={logins.isLoading}>
        <LoginTrend days={loginDays} />
      </Card>

      {/* ---- 최근 감사로그 ---- */}
      <Card
        size="small"
        title="최근 활동 (7일)"
        extra={
          <Link to="/app/admin-logs">
            <Button size="small" type="link">
              전체 로그 →
            </Button>
          </Link>
        }
      >
        <Table<AuditRow>
          rowKey="auditLogId"
          size="small"
          pagination={false}
          loading={audit.isLoading}
          dataSource={audit.data ?? []}
          locale={{ emptyText: '최근 기록이 없습니다.' }}
          columns={[
            {
              title: '일시',
              dataIndex: 'occurredAt',
              width: 150,
              render: (v: string) => new Date(v).toLocaleString('ko-KR'),
            },
            {
              title: '기업',
              dataIndex: 'companyName',
              width: 150,
              render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
            },
            {
              title: '분류',
              dataIndex: 'category',
              width: 90,
              render: (v: string) => <CodeTag codes={codes} group="AUDIT_CATEGORY" value={v} />,
            },
            { title: '동작', dataIndex: 'action', width: 200 },
            {
              title: '대상',
              dataIndex: 'resourceLabel',
              ellipsis: true,
              render: (v: string | null, row) => v ?? row.resourceType ?? '',
            },
            {
              title: '행위자',
              dataIndex: 'actorType',
              width: 100,
              render: (v: string, row) =>
                row.supportSessionId ? <Tag color="purple">지원 모드</Tag> : <Tag>{v}</Tag>,
            },
            {
              title: '결과',
              dataIndex: 'result',
              width: 90,
              render: (v: string) => (
                <Tag color={v === 'SUCCESS' ? 'green' : v === 'DENIED' ? 'volcano' : 'red'}>{v}</Tag>
              ),
            },
          ]}
        />
      </Card>

      {/* ---- 기업별 사용 현황 (상위 TOP_N 곳만) ---- */}
      <Card
        size="small"
        title={`기업별 사용 현황 — 상위 ${TOP_N}곳`}
        extra={
          <Space>
            <Radio.Group
              size="small"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              optionType="button"
              options={[
                { value: 'storage', label: '용량순' },
                { value: 'egress', label: '트래픽순' },
                { value: 'users', label: '사용자순' },
              ]}
            />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => void client.invalidateQueries({ queryKey: ['admin', 'dashboard'] })}
            />
          </Space>
        }
      >
        <Table<TenantUsage>
          rowKey="tenantId"
          columns={columns}
          dataSource={(tenants.data ?? []).slice(0, TOP_N)}
          loading={tenants.isLoading}
          size="small"
          // 잘라서 보여주므로 쪽 넘김은 없다. 전부 보려면 아래 링크로 간다.
          pagination={false}
          locale={{ emptyText: '기업이 없습니다.' }}
        />
        {/*
          잘렸다는 사실을 숨기지 않는다. 몇 곳 중 몇 곳인지 적어 두지 않으면
          "우리 고객이 10곳뿐인가" 로 읽힌다.
        */}
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          {(tenants.data ?? []).length > TOP_N ? (
            <>
              전체 {(tenants.data ?? []).length}곳 중 {currentSortLabel(sort)} 상위 {TOP_N}곳입니다.{' '}
              <Link to="/app/tenant-usage">기업별 사용량에서 전부 보기</Link>
            </>
          ) : (
            <>
              전체 {(tenants.data ?? []).length}곳입니다.{' '}
              <Link to="/app/tenant-usage">기업별 사용량에서 자세히 보기</Link>
            </>
          )}
        </Text>
      </Card>

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        이 화면은 집계값만 보여줍니다. 특정 기업의 구성원 정보가 필요하면 기업 관리에서
        지원 모드로 진입해야 하며, 그 접근은 고객사에 기록으로 남습니다.
      </Paragraph>
    </Space>
  );
}

/**
 * 한도 대비 사용량 막대.
 *
 * 한도가 없으면(요금제에 정의되지 않음) 막대 대신 수치만 보여준다.
 * 0을 기준으로 막대를 그리면 "한도 없음"과 "여유 있음"이 구분되지 않는다.
 */
function UsageBar({ used, limit, bytes }: { used: number; limit: number | null; bytes?: boolean }) {
  const fmt = bytes ? formatBytes : (v: number) => v.toLocaleString('ko-KR');

  if (limit === null || limit <= 0) {
    return (
      <Space direction="vertical" size={0}>
        <span>{fmt(used)}</span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          한도 없음
        </Text>
      </Space>
    );
  }

  const ratio = used / limit;
  return (
    <Space direction="vertical" size={0} style={{ width: '100%' }}>
      <Progress
        percent={Math.min(100, Math.round(ratio * 100))}
        size="small"
        status={ratio >= 1 ? 'exception' : undefined}
        strokeColor={ratio >= 0.8 ? '#faad14' : undefined}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {fmt(used)} / {fmt(limit)}
      </Text>
    </Space>
  );
}

/**
 * 로그인 추이를 막대로 그린다.
 *
 * 차트 라이브러리를 넣지 않았다. 14개 막대를 그리는 데 번들을 수백 KB 늘릴
 * 이유가 없다. 지표가 늘어 본격적인 차트가 필요해지면 그때 도입한다.
 */
function LoginTrend({ days }: { days: LoginDay[] }) {
  if (days.length === 0) return <Text type="secondary">데이터가 없습니다.</Text>;

  const max = Math.max(1, ...days.map((d) => d.success + d.failure));

  return (
    <Space align="end" size={6} style={{ width: '100%', height: 120 }}>
      {days.map((d) => {
        const total = d.success + d.failure;
        return (
          <Tooltip
            key={d.day}
            title={
              `${String(d.day).slice(5, 10)} · 성공 ${d.success}회 ` +
              `(${d.distinct_users}명) · 실패 ${d.failure}회`
            }
          >
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: 96, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                {d.failure > 0 && (
                  <div
                    style={{
                      height: `${(d.failure / max) * 96}px`,
                      background: '#ff4d4f',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                )}
                <div
                  style={{
                    height: `${(d.success / max) * 96}px`,
                    background: '#1677ff',
                    borderRadius: d.failure > 0 ? 0 : '2px 2px 0 0',
                    minHeight: total > 0 ? 2 : 0,
                  }}
                />
              </div>
              <Text type="secondary" style={{ fontSize: 10 }}>
                {String(d.day).slice(8, 10)}
              </Text>
            </div>
          </Tooltip>
        );
      })}
    </Space>
  );
}

const won = (v: number | null | undefined) => `${Number(v ?? 0).toLocaleString('ko-KR')}원`;

function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)}${units[i]}`;
}

/** "3일 전" 처럼 상대 시간으로. 정확한 시각은 툴팁에 있다. */
function relativeDays(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);

  if (days === 0) return '오늘';
  if (days === 1) return '어제';
  if (days < 30) return `${days}일 전`;
  if (days < 365) return `${Math.floor(days / 30)}개월 전`;
  return `${Math.floor(days / 365)}년 전`;
}
